import React, { useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../api';
import { formatMoney } from '../utils/currency';
import { percent } from '../utils/format';

const COLORS = ['#2f7d4c', '#2f6f9f', '#9a6700'];

export default function Dashboard({ onNavigate }) {
  const [stats, setStats] = useState(null);
  const [report, setReport] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [flock, setFlock] = useState(null);
  const [activeSheep, setActiveSheep] = useState([]);
  const [recentSheep, setRecentSheep] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    Promise.allSettled([
      api.getStats(),
      api.getMonthlyReport(6),
      api.listSheep('active'),
      api.getAlerts(30),
      api.getFlockReport(),
    ])
      .then(([statsResult, reportResult, sheepResult, alertsResult, flockResult]) => {
        if (!mounted) return;
        const failures = [statsResult, reportResult, sheepResult, alertsResult, flockResult]
          .filter(result => result.status === 'rejected');

        if (statsResult.status === 'fulfilled') setStats(statsResult.value);
        if (reportResult.status === 'fulfilled') setReport(reportResult.value);
        if (alertsResult.status === 'fulfilled') setAlerts(alertsResult.value);
        if (flockResult.status === 'fulfilled') setFlock(flockResult.value);
        if (sheepResult.status === 'fulfilled') {
          setActiveSheep(sheepResult.value);
          setRecentSheep([...sheepResult.value].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).slice(0, 5));
        }
        if (failures.length) {
          setError(failures[0].reason?.message || 'Some dashboard data could not be loaded.');
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const alertItems = useMemo(() => {
    if (!alerts) return [];
    const items = [];
    (alerts.vaccinations || []).forEach(v => items.push({
      key: `vacc-${v.id}`, sheep_id: v.sheep_id, tag_id: v.tag_id, icon: '💉',
      kind: v.overdue ? 'overdue' : 'due',
      text: `${v.vaccine_type} ${v.overdue ? 'overdue' : 'due'}`, date: v.due_date,
    }));
    (alerts.lambings || []).forEach(l => items.push({
      key: `lamb-${l.id}`, sheep_id: l.sheep_id, tag_id: l.tag_id, icon: '🐣',
      kind: l.overdue ? 'overdue' : 'due',
      text: `Lambing ${l.overdue ? 'overdue' : 'expected'}`, date: l.expected_lambing_date,
    }));
    (alerts.health_events || []).forEach(h => items.push({
      key: `health-${h.id}`, sheep_id: h.sheep_id, tag_id: h.tag_id, icon: '🩺',
      kind: 'open',
      text: `Open ${h.event_type}: ${h.description}`, date: h.date,
    }));
    const rank = { overdue: 0, due: 1, open: 2 };
    return items.sort((a, b) => (rank[a.kind] - rank[b.kind]) || (a.date || '').localeCompare(b.date || ''));
  }, [alerts]);

  const flockData = useMemo(() => {
    if (stats) {
      return [
        { name: 'Ewes', value: stats.ewes },
        { name: 'Rams', value: stats.rams },
        { name: 'Wethers', value: stats.wethers },
      ].filter(item => item.value > 0);
    }
    const counts = activeSheep.reduce((acc, sheep) => {
      acc[`${sheep.sex}s`] = (acc[`${sheep.sex}s`] || 0) + 1;
      return acc;
    }, { ewes: 0, rams: 0, wethers: 0 });
    return [
      { name: 'Ewes', value: counts.ewes },
      { name: 'Rams', value: counts.rams },
      { name: 'Wethers', value: counts.wethers },
    ].filter(item => item.value > 0);
  }, [activeSheep, stats]);

  const activeCount = stats?.total_active ?? activeSheep.length;

  const monthlyData = (report?.months || []).map(item => ({
    ...item,
    label: item.month.slice(5),
  }));

  return (
    <div className="page">
      <div className="dashboard-hero">
        <div className="hero-copy">
          <span className="eyebrow">Farm overview</span>
          <h1 className="page-title">Good decisions start with a clear flock.</h1>
          <p className="page-subtitle">See what needs attention, track performance, and move straight into today’s work.</p>
          <div className="hero-actions">
            <button className="btn btn-primary" onClick={() => onNavigate('flock')}>Manage flock <span aria-hidden="true">→</span></button>
            <button className="btn btn-secondary" onClick={() => onNavigate('finance')}>Open finances</button>
          </div>
        </div>
        <div className="hero-brief">
          <div className="hero-brief-head"><span>Operational status</span><strong>{alertItems.length ? `${alertItems.length} actions` : 'All clear'}</strong></div>
          <div className="hero-brief-row"><span className={`pulse ${stats?.open_health_events ? 'warning' : ''}`} /><div><strong>{stats?.open_health_events || 0} health issues</strong><small>Currently open</small></div></div>
          <div className="hero-brief-row"><span className={`pulse ${stats?.due_vaccinations_30d ? 'warning' : ''}`} /><div><strong>{stats?.due_vaccinations_30d || 0} vaccinations</strong><small>Due in the next 30 days</small></div></div>
          <div className="hero-brief-foot">
            <span>Active flock</span><strong>{activeCount}</strong>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger mb-4">{error}</div>}

      {stats && (
        <div className="stat-grid dashboard-stat-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.total_active}</div>
            <div className="stat-label">Animals in flock</div>
          </div>
          <div className={`stat-card ${stats.due_vaccinations_30d > 0 ? 'alert' : 'success'}`}>
            <div className="stat-value">{stats.due_vaccinations_30d}</div>
            <div className="stat-label">Vaccinations due</div>
          </div>
          <div className={`stat-card ${stats.open_health_events > 0 ? 'alert' : 'success'}`}>
            <div className="stat-value">{stats.open_health_events}</div>
            <div className="stat-label">Open health issues</div>
          </div>
          <div className={`stat-card ${stats.month_profit >= 0 ? 'success' : 'alert'}`}>
            <div className="stat-value">{formatMoney(stats.month_profit)}</div>
            <div className="stat-label">Net this month</div>
          </div>
        </div>
      )}

      <div className="dashboard-grid mb-4">
        <div className="card action-center">
          <div className="section-header">
            <span className="section-title">Action Center</span>
            <span className="section-note">{alertItems.length ? `${alertItems.length} need attention` : 'Next 30 days'}</span>
          </div>
          {loading && !alerts ? (
            <p className="text-muted">Loading tasks…</p>
          ) : alertItems.length === 0 ? (
            <div className="empty-state" style={{ padding: '24px 0' }}>
              <div className="empty-icon">✅</div>
              <p>Nothing needs attention. Flock is up to date.</p>
            </div>
          ) : (
            <ul className="alert-list">
              {alertItems.slice(0, 8).map(item => (
                <li key={item.key}>
                  <button type="button" className={`alert-item alert-${item.kind}`} onClick={() => onNavigate('profile', item.sheep_id)}>
                    <span className="alert-icon" aria-hidden="true">{item.icon}</span>
                    <span className="alert-body">
                      <strong>{item.tag_id}</strong>
                      <span className="alert-text">{item.text}</span>
                    </span>
                    <span className="alert-meta">{item.date || ''}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card">
          <div className="section-header">
            <span className="section-title">Flock Productivity</span>
            <span className="section-note">All time</span>
          </div>
          {flock ? (
            <div className="productivity-grid">
              <div><span className="productivity-value">{percent(flock.weaning_rate)}</span><span className="productivity-label">Weaning Rate</span></div>
              <div><span className="productivity-value">{flock.avg_litter_size || '—'}</span><span className="productivity-label">Avg Litter Size</span></div>
              <div><span className="productivity-value">{flock.lambs_per_ewe || '—'}</span><span className="productivity-label">Lambs / Ewe</span></div>
              <div><span className="productivity-value">{percent(flock.mortality_rate)}</span><span className="productivity-label">Mortality Rate</span></div>
              <div><span className="productivity-value">{flock.total_born}</span><span className="productivity-label">Lambs Born</span></div>
              <div><span className="productivity-value">{flock.total_weaned}</span><span className="productivity-label">Lambs Weaned</span></div>
            </div>
          ) : (
            <p className="text-muted">Loading…</p>
          )}
        </div>
      </div>

      <div className="dashboard-grid mb-4">
        <div className="card chart-card">
          <div className="section-header">
            <span className="section-title">Active Flock Composition</span>
            <span className="section-note">{loading && !stats ? 'Loading' : `${activeCount} active`}</span>
          </div>
          {loading && !stats ? (
            <div className="empty-chart">Loading flock...</div>
          ) : flockData.length === 0 ? (
            <div className="empty-chart">No active sheep yet.</div>
          ) : (
            <div className="composition-chart">
              <div className="composition-total">
                <strong>{activeCount}</strong>
                <span>active sheep</span>
              </div>
              <div className="mobile-chart-frame short">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={flockData} dataKey="value" nameKey="name" innerRadius={52} outerRadius={86} paddingAngle={4} animationDuration={950}>
                    {flockData.map((entry, index) => <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              </div>
              <div className="chart-legend">
                {flockData.map((item, index) => (
                  <span key={item.name}><i style={{ background: COLORS[index % COLORS.length] }} />{item.name}: {item.value}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="card chart-card">
          <div className="section-header">
            <span className="section-title">Income vs Expenses</span>
            <button className="btn btn-ghost btn-sm" onClick={() => onNavigate('finance')}>Finance →</button>
          </div>
          <div className="mobile-chart-frame short">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={monthlyData}>
              <defs>
                <linearGradient id="dashIncome" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2f7d4c" stopOpacity={0.34}/>
                  <stop offset="95%" stopColor="#2f7d4c" stopOpacity={0.02}/>
                </linearGradient>
                <linearGradient id="dashExpense" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#b54735" stopOpacity={0.24}/>
                  <stop offset="95%" stopColor="#b54735" stopOpacity={0.02}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#d7e0d5" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => formatMoney(v)} />
              <Area type="monotone" dataKey="income" stroke="#2f7d4c" fill="url(#dashIncome)" strokeWidth={2} animationDuration={900} />
              <Area type="monotone" dataKey="expenses" stroke="#b54735" fill="url(#dashExpense)" strokeWidth={2} animationDuration={900} />
            </AreaChart>
          </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="card">
          <div className="section-header">
            <span className="section-title">Recent Active Sheep</span>
            <button className="btn btn-ghost btn-sm" onClick={() => onNavigate('flock')}>View all →</button>
          </div>
          {recentSheep.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🐑</div>
              <p>No sheep added yet. <button className="btn btn-ghost btn-sm" onClick={() => onNavigate('flock')}>Add your first sheep</button></p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Tag ID</th>
                    <th>Breed</th>
                    <th>Sex</th>
                    <th>Date of Birth</th>
                    <th>Dam</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSheep.map(s => (
                    <tr key={s.id} onClick={() => onNavigate('profile', s.id)}>
                      <td data-label="Tag ID"><strong>{s.tag_id}</strong></td>
                      <td data-label="Breed">{s.breed || '—'}</td>
                      <td data-label="Sex"><span className={`badge badge-${s.sex}`}>{s.sex}</span></td>
                      <td data-label="Date of Birth">{s.date_of_birth || '—'}</td>
                      <td data-label="Dam">{s.dam_tag || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card chart-card">
          <div className="section-header">
            <span className="section-title">Monthly Net</span>
          </div>
          <div className="mobile-chart-frame short">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#d7e0d5" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v) => formatMoney(v)} />
              <Bar dataKey="profit" radius={[6, 6, 0, 0]} animationDuration={850}>
                {monthlyData.map((item) => (
                  <Cell key={item.month} fill={item.profit >= 0 ? '#2f7d4c' : '#b54735'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
